import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { type DateRange, DayPicker as RDP, type MonthCaptionProps, useDayPicker } from "react-day-picker";
import cn from "classnames";
import "react-day-picker/style.css";
import styles from "./DayPicker.module.scss";

export type ValidityDateRangeFieldsProps = {
  from: string;
  until: string;
  onChange: (from: string, until: string) => void;
  className?: string;
  /** `md` matches RateCard wells; `sm` matches import guided fields. */
  size?: "md" | "sm";
  /** Empty-state label on the trigger (default: "Not stated"). */
  emptyLabel?: string;
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const WEEKDAY_SHORT = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"] as const;

const parseIsoDate = (iso: string): Date | undefined => {
  if (!ISO_DATE.test(iso)) return undefined;
  const [year, month, day] = iso.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const toIsoDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/** Display ISO yyyy-mm-dd as dd.mm.yyyy (empty → ""). */
const formatDisplayDate = (iso: string): string => {
  if (!ISO_DATE.test(iso)) return "";
  const [year, month, day] = iso.split("-");
  return `${day}.${month}.${year}`;
};

/** Single-trigger label: full range, partial, or empty. */
const formatRangeLabel = (from: string, until: string, emptyLabel: string): { text: string; empty: boolean } => {
  const fromDisplay = formatDisplayDate(from);
  const untilDisplay = formatDisplayDate(until);
  if (fromDisplay && untilDisplay) {
    return { text: `${fromDisplay} – ${untilDisplay}`, empty: false };
  }
  if (fromDisplay) {
    return { text: `${fromDisplay} – …`, empty: false };
  }
  if (untilDisplay) {
    return { text: `… – ${untilDisplay}`, empty: false };
  }
  return { text: emptyLabel, empty: true };
};

/** Caption bar matching mockup `.rdc__top`: prev | centered month | next. */
const RangeMonthCaption = ({ calendarMonth, className }: MonthCaptionProps) => {
  const { goToMonth, previousMonth, nextMonth } = useDayPicker();
  const label = calendarMonth.date.toLocaleString("en-US", {
    month: "long",
    year: "numeric",
  });

  return (
    <div className={cn(className, styles.caption)}>
      <button
        type="button"
        className={styles.navBtn}
        aria-label="Previous month"
        disabled={!previousMonth}
        onClick={() => previousMonth && goToMonth(previousMonth)}
      >
        ‹
      </button>
      <div className={styles.monthLabel}>{label}</div>
      <button
        type="button"
        className={styles.navBtn}
        aria-label="Next month"
        disabled={!nextMonth}
        onClick={() => nextMonth && goToMonth(nextMonth)}
      >
        ›
      </button>
    </div>
  );
};

/** Mockup `.rdf__cal` calendar glyph. */
const CalendarIcon = () => (
  <svg
    className={styles.calIcon}
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <rect x="3" y="5" width="14" height="12" rx="2.5" />
    <path d="M3.4 9h13.2M7 3.4v3M13 3.4v3" />
  </svg>
);

/**
 * Single From–Until trigger that opens a floating range calendar (react-day-picker).
 * Emits ISO yyyy-mm-dd via onChange; closes on outside click, Escape, or a complete range.
 */
export const ValidityDateRangeFields = ({
  from,
  until,
  onChange,
  className,
  size = "md",
  emptyLabel = "Not stated",
}: ValidityDateRangeFieldsProps) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const popoverId = useId();

  const selected = useMemo((): DateRange | undefined => {
    const fromDate = parseIsoDate(from);
    const untilDate = parseIsoDate(until);
    if (!fromDate && !untilDate) return undefined;
    if (fromDate && untilDate) {
      return fromDate <= untilDate ? { from: fromDate, to: untilDate } : { from: untilDate, to: fromDate };
    }
    return { from: fromDate ?? untilDate, to: undefined };
  }, [from, until]);

  const anchorMonth = selected?.from ?? selected?.to ?? new Date();
  const [month, setMonth] = useState(anchorMonth);

  useEffect(() => {
    const next = parseIsoDate(from) ?? parseIsoDate(until);
    if (next) setMonth(next);
  }, [from, until]);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) {
        close();
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        close();
      }
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [close, open]);

  const awaitingEndRef = useRef(false);

  const toggle = () =>
    setOpen((current) => {
      if (!current) {
        awaitingEndRef.current = false;
      }
      return !current;
    });

  const handleSelect = (range: DateRange | undefined) => {
    if (!range?.from) {
      onChange("", "");
      awaitingEndRef.current = false;
      return;
    }
    const f = toIsoDate(range.from);
    const t = range.to ? toIsoDate(range.to) : undefined;

    if (!awaitingEndRef.current) {
      // First pick: start of a new range (RDP often sets to === from)
      awaitingEndRef.current = true;
      onChange(f, "");
      return;
    }

    // Second pick: complete range (same day allowed via second click)
    const end = t ?? f;
    const [a, b] = end < f ? [end, f] : [f, end];
    onChange(a, b);
    awaitingEndRef.current = false;
    close();
  };

  const { text: rangeLabel, empty: labelEmpty } = formatRangeLabel(from, until, emptyLabel);
  const hint = from && !until ? "Pick the end date" : "Pick the start date";

  return (
    <div
      ref={wrapRef}
      className={cn(styles.fields, styles[`fields--${size}`], className, { [styles["fields--open"]]: open })}
    >
      <button
        type="button"
        className={cn(styles.trigger, { [styles["trigger--active"]]: open })}
        data-validity-trigger
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        onClick={toggle}
      >
        <CalendarIcon />
        <span className={cn(styles.triggerValue, { [styles["triggerValue--empty"]]: labelEmpty })}>{rangeLabel}</span>
      </button>

      {open && (
        <div id={popoverId} className={styles.popover} role="dialog" aria-label="Validity date range">
          <RDP
            animate={false}
            mode="range"
            weekStartsOn={1}
            hideNavigation
            selected={selected}
            month={month}
            onMonthChange={setMonth}
            onSelect={handleSelect}
            className={styles.picker}
            components={{ MonthCaption: RangeMonthCaption }}
            formatters={{
              formatWeekdayName: (date) => WEEKDAY_SHORT[date.getDay()],
            }}
          />
          <p className={styles.hint}>{hint}</p>
        </div>
      )}
    </div>
  );
};
